import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { gamesApi, usersApi, venuesApi } from '../services/api';
import './GameDetails.css';

function GameDetails({ user, onLogout }) {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [seating, setSeating] = useState(null);
  const [results, setResults] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tables');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [venues, setVenues] = useState([]);

  // Inline edit mode for game info
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    gameType: '',
    venueId: '',
    startDate: '',
    startTime: '',
    maxPlaces: 9,
  });

  // Dealer assignment modal
  const [showDealerModal, setShowDealerModal] = useState(false);
  const [dealerModalTable, setDealerModalTable] = useState(null);
  const [selectedDealerId, setSelectedDealerId] = useState('');

  useEffect(() => {
    fetchGameData();
  }, [gameId]);

  const fetchGameData = async () => {
    try {
      setLoading(true);

      const [seatingRes, resultsRes, dealersRes, venuesRes] = await Promise.all([
        gamesApi.getSeating(gameId).catch(() => null),
        gamesApi.getResults(gameId).catch(() => null),
        usersApi.getDealers().catch(() => ({ data: { dealers: [] } })),
        venuesApi.getAll().catch(() => ({ data: [], venues: [] })),
      ]);

      const seatingData = seatingRes?.data || seatingRes;
      if (seatingData?.game) {
        setGame(seatingData.game);
        setSeating(seatingData.tables || []);
      }

      const resultsData = resultsRes?.data || resultsRes;
      if (resultsData?.results) {
        setResults(resultsData.results);
      }

      const dealersList = dealersRes?.data?.dealers || dealersRes?.dealers || [];
      setDealers(Array.isArray(dealersList) ? dealersList : []);

      const venuesList = venuesRes?.data?.venues || venuesRes?.venues || venuesRes?.data || [];
      setVenues(Array.isArray(venuesList) ? venuesList : []);

    } catch (err) {
      console.error('Error fetching game data:', err);
      setError('Failed to load game details');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  // --- Table Management ---
  const handleAddTable = async () => {
    try {
      setSaving(true);
      await gamesApi.addTable(gameId, game?.maxPlaces || 10);
      showToast('success', 'Table added');
      fetchGameData();
    } catch (err) {
      console.error('Error adding table:', err);
      showToast('error', err.message || 'Failed to add table');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTable = async (tableNumber) => {
    if (!window.confirm(`Remove Table ${tableNumber}? This cannot be undone.`)) return;
    try {
      setSaving(true);
      await gamesApi.removeTable(gameId, tableNumber);
      showToast('success', `Table ${tableNumber} removed`);
      fetchGameData();
    } catch (err) {
      console.error('Error removing table:', err);
      showToast('error', err.message || 'Failed to remove table');
    } finally {
      setSaving(false);
    }
  };

  // --- Dealer Management ---
  const handleOpenDealerModal = (tableNumber) => {
    setDealerModalTable(tableNumber);
    setSelectedDealerId('');
    setShowDealerModal(true);
  };

  const handleAssignDealer = async () => {
    if (!selectedDealerId) return;
    try {
      setSaving(true);
      await gamesApi.assignDealerToTable(gameId, selectedDealerId, dealerModalTable);
      showToast('success', 'Dealer assigned');
      setShowDealerModal(false);
      fetchGameData();
    } catch (err) {
      console.error('Error assigning dealer:', err);
      showToast('error', err.message || 'Failed to assign dealer');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDealer = async (dealerId, dealerName) => {
    if (!window.confirm(`Remove dealer ${dealerName}?`)) return;
    try {
      setSaving(true);
      await gamesApi.removeDealerFromGame(gameId, dealerId);
      showToast('success', 'Dealer removed');
      fetchGameData();
    } catch (err) {
      console.error('Error removing dealer:', err);
      showToast('error', err.message || 'Failed to remove dealer');
    } finally {
      setSaving(false);
    }
  };

  // --- Game Info Edit ---
  const handleStartEdit = () => {
    const startDateTime = new Date(game.startTime);
    const dateStr = startDateTime.toISOString().split('T')[0];
    const timeStr = startDateTime.toTimeString().slice(0, 5);

    setEditFormData({
      gameType: game.gameType || 'Texas Holdem',
      venueId: game.venue?.id || game.venueId || '',
      startDate: dateStr,
      startTime: timeStr,
      maxPlaces: game.maxPlaces || 9,
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveGameInfo = async () => {
    try {
      setSaving(true);
      const startDateTime = new Date(`${editFormData.startDate}T${editFormData.startTime}:00`);

      await gamesApi.update(gameId, {
        gameType: editFormData.gameType,
        venueId: editFormData.venueId,
        startTime: startDateTime.toISOString(),
        maxPlaces: parseInt(editFormData.maxPlaces),
      });

      showToast('success', 'Game updated');
      setEditMode(false);
      fetchGameData();
    } catch (err) {
      console.error('Error updating game:', err);
      showToast('error', err.message || 'Failed to update game');
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (dateStr, timeZone) => {
    const date = new Date(dateStr);
    const options = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    if (timeZone) options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  const getOrdinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="main-content">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="main-content">
          <div className="error-message">{error || 'Game not found'}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/games')}>
            Back to Games
          </button>
        </main>
      </div>
    );
  }

  // Build a map of dealer assignments by table number
  const dealerByTable = {};
  if (seating) {
    seating.forEach(table => {
      if (table.dealer) {
        dealerByTable[table.tableNumber] = table.dealer;
      }
    });
  }

  const totalOccupied = seating ? seating.reduce((sum, t) => sum + t.seats.filter(s => s.isOccupied).length, 0) : 0;

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/games')}>
              ← Back to Games
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/kiosk?gameId=${gameId}`)}>
              Kiosk Mode
            </button>
          </div>
          <h2>{game.gameType} at {game.venue?.name || game.location?.name}</h2>
          <p>{formatDateTime(game.startTime, game.leagueTimezone)}</p>
        </div>

        {/* Game Info Card */}
        <div className="card game-info-card">
          {editMode ? (
            /* Edit Mode */
            <div className="game-info-edit">
              <div className="game-info-edit-grid">
                <div className="form-group">
                  <label htmlFor="editGameType">Game Type</label>
                  <select
                    id="editGameType"
                    name="gameType"
                    className="form-select"
                    value={editFormData.gameType}
                    onChange={handleEditInputChange}
                  >
                    <option value="Texas Holdem">Texas Holdem</option>
                    <option value="Omaha">Omaha</option>
                    <option value="Seven Card Stud">Seven Card Stud</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="editVenueId">Venue</label>
                  <select
                    id="editVenueId"
                    name="venueId"
                    className="form-select"
                    value={editFormData.venueId}
                    onChange={handleEditInputChange}
                  >
                    <option value="">Select a venue</option>
                    {venues.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="editStartDate">Date</label>
                  <input
                    type="date"
                    id="editStartDate"
                    name="startDate"
                    className="form-input"
                    value={editFormData.startDate}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editStartTime">Time</label>
                  <input
                    type="time"
                    id="editStartTime"
                    name="startTime"
                    className="form-input"
                    value={editFormData.startTime}
                    onChange={handleEditInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editMaxPlaces">Max Players</label>
                  <input
                    type="number"
                    id="editMaxPlaces"
                    name="maxPlaces"
                    className="form-input"
                    value={editFormData.maxPlaces}
                    onChange={handleEditInputChange}
                    min="2"
                    max="100"
                  />
                </div>
              </div>
              <div className="game-info-edit-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveGameInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            /* Display Mode */
            <>
              <div className="game-info-header">
                <button className="btn btn-secondary btn-sm" onClick={handleStartEdit}>
                  Edit Game Info
                </button>
              </div>
              <div className="game-info-grid">
                <div className="game-info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{game.venue?.name || game.location?.name}</span>
                  <span className="info-detail">{game.venue?.address || game.location?.address}</span>
                </div>
                <div className="game-info-item">
                  <span className="info-label">Tables</span>
                  <span className="info-value">{seating ? seating.length : 0}</span>
                </div>
                <div className="game-info-item">
                  <span className="info-label">Players</span>
                  <span className="info-value">{totalOccupied} / {game.maxPlaces}</span>
                </div>
                <div className="game-info-item">
                  <span className="info-label">Status</span>
                  <span className="info-value">
                    {new Date(game.startTime) > new Date() ? (
                      <span className="badge badge-info">Upcoming</span>
                    ) : (
                      <span className="badge badge-success">Completed</span>
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'tables' ? 'active' : ''}`}
            onClick={() => setActiveTab('tables')}
          >
            Tables & Dealers
          </button>
          <button
            className={`tab ${activeTab === 'seating' ? 'active' : ''}`}
            onClick={() => setActiveTab('seating')}
          >
            Seating Chart
          </button>
          <button
            className={`tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            Results
          </button>
        </div>

        {/* Tables & Dealers Tab */}
        {activeTab === 'tables' && (
          <div className="card">
            <div className="card-header">
              <h3>Tables & Dealers</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddTable}
                disabled={saving}
              >
                + Add Table
              </button>
            </div>

            {seating && seating.length > 0 ? (
              <div className="tables-management">
                {seating.map(table => {
                  const tableDealer = dealerByTable[table.tableNumber];
                  const occupiedCount = table.seats.filter(s => s.isOccupied).length;

                  return (
                    <div key={table.tableId} className="table-manage-card">
                      <div className="table-manage-header">
                        <div className="table-manage-title">
                          <h4>Table {table.tableNumber}</h4>
                          <span className="table-manage-info">
                            {occupiedCount}/{table.maxPlayers} players
                          </span>
                        </div>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveTable(table.tableNumber)}
                          disabled={saving || seating.length <= 1}
                          title={seating.length <= 1 ? 'Cannot remove the last table' : 'Remove table'}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="table-manage-dealer">
                        <span className="dealer-section-label">Dealer:</span>
                        {tableDealer ? (
                          <div className="dealer-assignment">
                            <span className="badge badge-success">{tableDealer.name}</span>
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleRemoveDealer(tableDealer.id, tableDealer.name)}
                              title="Remove dealer"
                              disabled={saving}
                            >
                              ✕
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleOpenDealerModal(table.tableNumber)}
                              disabled={saving}
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleOpenDealerModal(table.tableNumber)}
                            disabled={saving}
                          >
                            Assign Dealer
                          </button>
                        )}
                      </div>

                      {/* Quick seat preview */}
                      <div className="table-manage-seats">
                        {table.seats.slice(0, table.maxPlayers).map(seat => (
                          <div
                            key={seat.seatNumber}
                            className={`mini-seat ${seat.isOccupied ? 'occupied' : 'empty'}`}
                            title={seat.isOccupied ? seat.playerName : `Seat ${seat.seatNumber} - Empty`}
                          >
                            {seat.seatNumber}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🪑</div>
                <h3>No tables</h3>
                <p>Add a table to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Seating Tab */}
        {activeTab === 'seating' && (
          <div className="card">
            <div className="card-header">
              <h3>Seating Chart</h3>
            </div>

            {seating && seating.length > 0 ? (
              seating.map(table => (
                <div key={table.tableId} className="table-seating">
                  <h4>
                    Table {table.tableNumber}
                    {dealerByTable[table.tableNumber] && (
                      <span className="table-dealer-label"> — Dealer: {dealerByTable[table.tableNumber].name}</span>
                    )}
                  </h4>
                  <div className="seats-grid">
                    {table.seats.map(seat => (
                      <div
                        key={seat.seatNumber}
                        className={`seat-card ${seat.isOccupied ? 'occupied' : 'empty'}`}
                      >
                        <div className="seat-number">Seat {seat.seatNumber}</div>
                        {seat.isOccupied ? (
                          <div className="player-name">{seat.playerName}</div>
                        ) : (
                          <div className="empty-label">Empty</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🪑</div>
                <h3>No seating data</h3>
                <p>No players have registered for this game yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="card">
            <div className="card-header">
              <h3>Game Results</h3>
            </div>

            {results && results.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Place</th>
                    <th>Player</th>
                    <th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={result.playerId || index}>
                      <td>
                        <span className={`place-badge place-${result.finishPosition ?? result.place}`}>
                          {getOrdinalSuffix(result.finishPosition ?? result.place)}
                        </span>
                      </td>
                      <td><strong>{result.playerName}</strong></td>
                      <td>{(result.pointsAwarded ?? result.points) || 0} pts</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🏆</div>
                <h3>No results yet</h3>
                <p>Game results will appear here after the game is completed.</p>
              </div>
            )}
          </div>
        )}

        {/* Assign Dealer Modal */}
        {showDealerModal && (
          <div className="modal-overlay" onClick={() => setShowDealerModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Assign Dealer{dealerModalTable ? ` to Table ${dealerModalTable}` : ''}</h3>
                <button className="modal-close" onClick={() => setShowDealerModal(false)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="dealerSelect">Dealer</label>
                  <select
                    id="dealerSelect"
                    className="form-select"
                    value={selectedDealerId}
                    onChange={(e) => setSelectedDealerId(e.target.value)}
                  >
                    <option value="">Select a dealer</option>
                    {dealers.map(dealer => (
                      <option key={dealer.id} value={dealer.id}>
                        {dealer.firstName && dealer.lastName
                          ? `${dealer.firstName} ${dealer.lastName}`
                          : dealer.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDealerModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAssignDealer}
                  disabled={!selectedDealerId || saving}
                >
                  {saving ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`gd-toast ${toast.type}`}>{toast.message}</div>
        )}
      </main>
    </div>
  );
}

export default GameDetails;
